import React, { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import api, { fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ModuleAccessTab } from "@/pages/ModuleAccessTab";
import { toast } from "sonner";
import { PageHeader } from "@/components/common";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import ChatSettings from "@/components/ChatSettings";
import AnnouncementsAdmin from "@/components/AnnouncementsAdmin";
import DataManagement from "@/components/DataManagement";
import MfaCard from "@/components/MfaCard";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const GROUPS = [
  { name: "People & Access", items: [
    ["users", "Users", Icons.UserCog, "Create and manage user accounts, assign roles and reset passwords.", "bg-blue-50 text-blue-600"],
    ["roles", "Roles & Permissions", Icons.ShieldCheck, "Create roles and choose exactly what each one can see and do.", "bg-purple-50 text-purple-600"],
    ["modules", "Module Access", Icons.LayoutGrid, "Hide a module from everyone except Super Admin and selected users.", "bg-sky-50 text-sky-600"],
    ["workflow", "Approval Workflow", Icons.GitBranch, "Decide who approves what across modules, with amount limits.", "bg-emerald-50 text-emerald-600"],
  ] },
  { name: "Organization", items: [
    ["company", "Companies", Icons.Building2, "Add and manage the companies in your group.", "bg-amber-50 text-amber-600"],
  ] },
  { name: "Communication", items: [
    ["chat", "Chat", Icons.MessagesSquare, "Turn chat features on or off and set chat rules.", "bg-cyan-50 text-cyan-600"],
    ["announcements", "Announcements", Icons.Megaphone, "Post company-wide announcements to employees.", "bg-pink-50 text-pink-600"],
  ] },
  { name: "Appearance", items: [
    ["branding", "Form Branding", Icons.Palette, "System name, logo, colours and the footer on printed forms.", "bg-rose-50 text-rose-600"],
  ] },
  { name: "System & Security", items: [
    ["security", "Security", Icons.Lock, "Session timeout, password rules, lockouts and two-step login.", "bg-indigo-50 text-indigo-600"],
    ["data", "Data Management", Icons.Database, "Back up, restore and clean up system data safely.", "bg-teal-50 text-teal-600"],
    ["audit", "Audit Trail", Icons.ScrollText, "See who changed what and when, across the whole system.", "bg-slate-100 text-slate-600"],
    ["handbook", "Tech Handbook", Icons.BookOpen, "Confidential technical documentation for IT.", "bg-orange-50 text-orange-600"],
  ] },
];
const ALL = GROUPS.flatMap((g) => g.items);

/* ---------------- Overview ---------------- */
function Overview({ go, visible }) {
  const [stats, setStats] = useState({});
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);
  return (
    <div className="space-y-7">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {[["Users", stats.users, Icons.Users, "users"], ["Employees", stats.employees, Icons.UserCheck, null], ["Companies", stats.companies, Icons.Building2, "company"], ["Roles", stats.roles, Icons.ShieldCheck, "roles"], ["Audit Logs", stats.audit_entries, Icons.ScrollText, "audit"]].map(([l, v, I, k]) => (
          <button key={l} disabled={!k} onClick={() => k && go(k)} className="text-left bg-white rounded-xl border border-slate-200 p-4 transition-shadow enabled:hover:shadow-md" data-testid={`settings-stat-${l.toLowerCase().replace(" ", "-")}`}>
            <div className="flex items-center justify-between"><span className="text-xs text-slate-500">{l}</span><I className="h-4 w-4 text-blue-600" /></div>
            <div className="text-2xl font-semibold text-slate-900 mt-1 tabular-nums">{v ?? "—"}</div>
          </button>
        ))}
      </div>
      {GROUPS.map((g) => {
        const items = g.items.filter(([k]) => visible(k));
        if (!items.length) return null;
        return (
          <section key={g.name}>
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">{g.name}</h3>
            <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
              {items.map(([key, title, Icon, desc, color]) => (
                <button key={key} onClick={() => go(key)} className="group text-left bg-white rounded-xl border border-slate-200 p-4 transition-all hover:shadow-md hover:border-blue-200" data-testid={`settings-open-${key}`}>
                  <div className="flex items-start gap-3">
                    <div className={`h-10 w-10 shrink-0 rounded-lg flex items-center justify-center ${color}`}><Icon className="h-5 w-5" /></div>
                    <div className="min-w-0"><div className="font-semibold text-slate-900 flex items-center gap-1">{title}<Icons.ChevronRight className="h-4 w-4 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-blue-500" /></div><p className="text-xs text-slate-500 mt-1 leading-relaxed">{desc}</p></div>
                  </div>
                </button>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

/* ---------------- Users ---------------- */
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [roles, setRoles] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", username: "", email: "", password: "", role: "EMPLOYEE", company_id: "", department: "", position: "" });

  const load = useCallback(() => { api.get(`/admin/users?q=${encodeURIComponent(q)}`).then(({ data }) => setUsers(data)); }, [q]);
  useEffect(() => {
    api.get("/companies").then(({ data }) => { setCompanies(data); if (data[0]) setForm((f) => ({ ...f, company_id: data[0].id })); });
    api.get("/admin/roles").then(({ data }) => setRoles(data)).catch(() => {});
  }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const create = async () => {
    try { await api.post("/admin/users", form); toast.success("User created"); setOpen(false); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const toggleStatus = async (u) => {
    await api.put(`/admin/users/${u.id}`, { status: u.status === "Active" ? "Suspended" : "Active" });
    load();
  };
  const [resetFor, setResetFor] = useState(null);
  const [removeFor, setRemoveFor] = useState(null);
  const locked = (u) => ["SYSTEM ADMIN", "SUPER ADMIN"].includes(u.role) || (Array.isArray(u.permissions) && u.permissions.includes("*"));
  const doRemove = async () => {
    try { await api.delete(`/admin/users/${removeFor.id}`); toast.success(`${removeFor.full_name} removed`); setRemoveFor(null); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Remove failed"); }
  };
  const [editFor, setEditFor] = useState(null);
  const saveEdit = async () => {
    const { id, full_name, email, role, company_id, department, position, _co0 } = editFor;
    const body = { full_name, email, role, department, position, ...(company_id !== _co0 ? { company_id } : {}) };
    try { await api.put(`/admin/users/${id}`, body); toast.success("User updated"); setEditFor(null); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };
  const [resetResult, setResetResult] = useState(null);
  const resetPw = async () => {
    try {
      const { data } = await api.post(`/admin/users/${resetFor.id}/reset-password`);
      setResetFor(null);
      setResetResult(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Reset failed"); }
  };
  const copyPw = async () => {
    try { await navigator.clipboard.writeText(resetResult.temporary_password); toast.success("Password copied"); }
    catch { toast.error("Copy failed - select the password and copy it manually"); }
  };

  return (
    <div>
      <div className="flex justify-between gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users…" className="pl-9" data-testid="users-search" />
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-user-button"><Icons.Plus className="h-4 w-4 mr-1.5" /> Add User</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create User</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2 col-span-2"><Label>Full Name</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="user-fullname" /></div>
              <div className="space-y-2"><Label>Username</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="user-username" /></div>
              <div className="space-y-2"><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
              <div className="space-y-2"><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></div>
              <div className="space-y-2"><Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{roles.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2 col-span-2"><Label>Company</Label>
                <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                  <SelectTrigger data-testid="user-company"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="__all__" data-testid="user-company-all">All Companies</SelectItem>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Department</Label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></div>
              <div className="space-y-2"><Label>Position</Label><Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={create} className="bg-blue-600 hover:bg-blue-700" data-testid="user-create-submit">Create User</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead>User</TableHead><TableHead className="hidden md:table-cell">Username</TableHead>
            <TableHead>Role</TableHead><TableHead className="hidden lg:table-cell">Last Login</TableHead>
            <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody data-testid="users-table">
            {users.map((u) => (
              <TableRow key={u.id}>
                <TableCell><div className="font-medium text-slate-800 text-sm">{u.full_name}</div><div className="text-xs text-slate-500">{u.email}</div></TableCell>
                <TableCell className="hidden md:table-cell font-mono text-xs">{u.username}</TableCell>
                <TableCell><Badge variant="secondary" className="text-[10px]">{u.role}</Badge>{u.all_companies && <Badge variant="outline" className="text-[10px] ml-1" data-testid={`all-co-${u.username}`}>All Companies</Badge>}</TableCell>
                <TableCell className="hidden lg:table-cell text-xs text-slate-500">{u.last_login ? fmtDateTime(u.last_login) : "Never"}</TableCell>
                <TableCell><Badge className={`text-[10px] ${u.status === "Active" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"} hover:opacity-100`}>{u.status}</Badge></TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1.5 justify-end">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { const co = u.all_companies ? "__all__" : (u.company_id || ""); setEditFor({ ...u, department: u.department || "", position: u.position || "", company_id: co, _co0: co }); }} data-testid={`edit-user-${u.username}`}><Icons.Pencil className="h-3 w-3 mr-1" />Edit</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setResetFor(u)} data-testid={`reset-pw-${u.username}`}>Reset PW</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => toggleStatus(u)} data-testid={`toggle-${u.username}`}>{u.status === "Active" ? "Suspend" : "Activate"}</Button>
                    {locked(u)
                      ? <Button size="sm" variant="outline" disabled className="h-7 text-xs" title="System Admin accounts are locked" data-testid={`remove-locked-${u.username}`}><Icons.Lock className="h-3 w-3 mr-1" />Locked</Button>
                      : <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setRemoveFor(u)} data-testid={`remove-user-${u.username}`}><Icons.Trash2 className="h-3 w-3 mr-1" />Remove</Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={!!removeFor} onOpenChange={(o) => !o && setRemoveFor(null)}>
        <DialogContent data-testid="remove-user-dialog">
          <DialogHeader><DialogTitle>Remove user?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600"><b>{removeFor?.full_name}</b> ({removeFor?.username}) will be signed out and can no longer log in. Their username and email become free to use again. Past records they created stay in the system.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveFor(null)} data-testid="remove-user-cancel">Cancel</Button>
            <Button onClick={doRemove} className="bg-red-600 hover:bg-red-700" data-testid="remove-user-confirm">Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!editFor} onOpenChange={(o) => !o && setEditFor(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle>Edit User</DialogTitle></DialogHeader>
          {editFor && <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2 col-span-2"><Label>Full Name</Label><Input value={editFor.full_name || ""} onChange={(e) => setEditFor({ ...editFor, full_name: e.target.value })} data-testid="edit-user-fullname" /></div>
            <div className="space-y-2"><Label>Username</Label><Input value={editFor.username} disabled data-testid="edit-user-username" /></div>
            <div className="space-y-2"><Label>Email</Label><Input value={editFor.email || ""} onChange={(e) => setEditFor({ ...editFor, email: e.target.value })} data-testid="edit-user-email" /></div>
            <div className="space-y-2"><Label>Role</Label>
              <Select value={editFor.role} onValueChange={(v) => setEditFor({ ...editFor, role: v })}>
                <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{roles.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Company</Label>
              <Select value={editFor.company_id} onValueChange={(v) => setEditFor({ ...editFor, company_id: v })}>
                <SelectTrigger data-testid="edit-user-company"><SelectValue placeholder="Select company" /></SelectTrigger>
                <SelectContent><SelectItem value="__all__" data-testid="edit-user-company-all">All Companies</SelectItem>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Department</Label><Input value={editFor.department} onChange={(e) => setEditFor({ ...editFor, department: e.target.value })} data-testid="edit-user-department" /></div>
            <div className="space-y-2"><Label>Position</Label><Input value={editFor.position} onChange={(e) => setEditFor({ ...editFor, position: e.target.value })} data-testid="edit-user-position" /></div>
            <p className="col-span-2 text-xs text-slate-500">Changing the role replaces this user's permissions with that role's permissions. Use Reset PW to change the password.</p>
            {editFor.company_id !== editFor._co0 && editFor.company_id !== "__all__" && (editFor.all_companies || (editFor.company_ids || []).length > 1) &&
              <p className="col-span-2 text-xs text-amber-700 bg-amber-50 rounded-md px-2 py-1.5" data-testid="edit-user-company-warning">This user can currently open {editFor.all_companies ? "all companies" : `${editFor.company_ids.length} companies`}. Saving limits them to the selected company only.</p>}
          </div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditFor(null)} data-testid="edit-user-cancel">Cancel</Button>
            <Button onClick={saveEdit} className="bg-blue-600 hover:bg-blue-700" data-testid="edit-user-save">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!resetFor} onOpenChange={(o) => !o && setResetFor(null)}>
        <DialogContent data-testid="reset-pw-confirm-dialog">
          <DialogHeader><DialogTitle>Reset password?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">A new password will be generated for <b>{resetFor?.full_name}</b>. Their current password stops working and they are signed out. On next login they must enter the reset password, then set a new password.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetFor(null)} data-testid="reset-pw-cancel">Cancel</Button>
            <Button onClick={resetPw} className="bg-blue-600 hover:bg-blue-700" data-testid="reset-pw-generate">Generate Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!resetResult} onOpenChange={(o) => !o && setResetResult(null)}>
        <DialogContent data-testid="reset-pw-result-dialog">
          <DialogHeader><DialogTitle>Password reset</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Share this reset password with <b>{resetResult?.full_name}</b> ({resetResult?.username}). It is shown only once.</p>
          <div className="flex gap-2">
            <Input readOnly value={resetResult?.temporary_password || ""} onFocus={(e) => e.target.select()} className="font-mono" data-testid="reset-pw-value" />
            <Button variant="outline" onClick={copyPw} data-testid="reset-pw-copy"><Icons.Copy className="h-4 w-4 mr-1.5" />Copy</Button>
          </div>
          <DialogFooter><Button onClick={() => setResetResult(null)} className="bg-slate-900 hover:bg-slate-800" data-testid="reset-pw-done">Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Roles & Permissions ---------------- */
function RolesTab() {
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [edit, setEdit] = useState(null);

  const load = () => api.get("/admin/roles").then(({ data }) => setRoles(data));
  useEffect(() => { load(); api.get("/permissions/catalog").then(({ data }) => setCatalog(data)); }, []);

  const perms = edit ? (edit.permissions === "*" ? "ALL" : edit.permissions) : [];
  const toggle = (code) => {
    if (perms === "ALL") return;
    const has = perms.includes(code);
    setEdit({ ...edit, permissions: has ? perms.filter((p) => p !== code) : [...perms, code] });
  };
  const save = async () => {
    try { await api.put(`/admin/roles/${edit.id}`, { name: edit.name, description: edit.description, permissions: edit.permissions }); toast.success("Role updated"); setEdit(null); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b text-sm font-medium text-slate-800">Roles ({roles.length})</div>
        <div className="divide-y divide-slate-100 max-h-[560px] overflow-y-auto" data-testid="roles-list">
          {roles.map((r) => (
            <button key={r.id} onClick={() => setEdit(r.permissions === "*" ? { ...r, permissions: "*" } : r)} className={`w-full text-left px-5 py-3 hover:bg-slate-50 flex items-center justify-between ${edit?.id === r.id ? "bg-blue-50" : ""}`} data-testid={`role-${r.code}`}>
              <div>
                <div className="text-sm font-medium text-slate-800">{r.name} {r.is_system && <Badge variant="outline" className="ml-1 text-[9px]">System</Badge>}</div>
                <div className="text-xs text-slate-500">{r.description}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs text-slate-400">{r.user_count} users</div>
                <div className="text-[11px] text-slate-400">{r.permissions === "*" ? "All perms" : (() => { const n = r.permissions.filter((p) => catalog.some((m) => m.permissions.some((x) => x.code === p))).length; return `${n} perm${n === 1 ? "" : "s"}`; })()}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        {!edit ? <div className="text-sm text-slate-400 text-center py-12">Select a role to configure permissions.</div> : (
          <>
            <div className="flex items-center justify-between mb-4">
              <div className="font-medium text-slate-800">{edit.name}</div>
              {perms !== "ALL" && <Button size="sm" onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="save-role">Save</Button>}
            </div>
            {perms === "ALL" ? <div className="text-sm text-amber-600 bg-amber-50 rounded-lg p-3">This role has unrestricted access to all permissions.</div> : (
              <div className="space-y-4 max-h-[480px] overflow-y-auto pr-2">
                {catalog.map((m) => (
                  <div key={m.module}>
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">{m.module}</div>
                    {m.note && <div className="text-[11px] text-slate-500 bg-slate-50 rounded-md px-2 py-1.5 mb-2" data-testid={`perm-note-${m.module}`}>{m.note}</div>}
                    <div className="grid sm:grid-cols-2 gap-2">
                      {m.permissions.map((p) => (
                        <label key={p.code} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer" data-testid={`perm-${p.code}`}>
                          <Checkbox checked={perms.includes(p.code)} onCheckedChange={() => toggle(p.code)} />
                          {p.label}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- Approval Workflow + Matrix ---------------- */
function WorkflowTab() {
  const [workflows, setWorkflows] = useState([]);
  const [matrix, setMatrix] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ transaction_type: "Purchase Request", min_amount: 0, max_amount: 50000, approver_role: "Department Head", sequence: 1 });

  const load = () => { api.get("/admin/workflows").then(({ data }) => setWorkflows(data)); api.get("/admin/approval-matrix").then(({ data }) => setMatrix(data)); };
  useEffect(() => { load(); }, []);
  const addMatrix = async () => {
    try { await api.post("/admin/approval-matrix", { ...form, min_amount: Number(form.min_amount), max_amount: Number(form.max_amount), sequence: Number(form.sequence) }); toast.success("Rule added"); setOpen(false); load(); }
    catch (err) { toast.error("Failed"); }
  };
  const delMatrix = async (m) => { await api.delete(`/admin/approval-matrix/${m.id}`); load(); };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-sm font-medium text-slate-800 mb-3">Workflow Definitions</div>
        <div className="grid md:grid-cols-2 gap-4">
          {workflows.map((w) => (
            <div key={w.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`workflow-${w.module}`}>
              <div className="flex items-center justify-between mb-3">
                <div><div className="font-medium text-slate-800">{w.name}</div><div className="text-xs text-slate-500">{w.module} · {w.transaction_type}</div></div>
                <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 text-[10px]">Active</Badge>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {w.steps.map((s, i) => (
                  <React.Fragment key={i}>
                    <span className="px-2.5 py-1 rounded-md bg-slate-100 text-xs text-slate-700">{s.approver_type}</span>
                    {i < w.steps.length - 1 && <Icons.ArrowRight className="h-3.5 w-3.5 text-slate-300" />}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-medium text-slate-800">Approval Matrix</div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="add-matrix-rule"><Icons.Plus className="h-4 w-4 mr-1" /> Add Rule</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Approval Rule</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2 col-span-2"><Label>Transaction Type</Label><Input value={form.transaction_type} onChange={(e) => setForm({ ...form, transaction_type: e.target.value })} /></div>
                <div className="space-y-2"><Label>Min Amount</Label><Input type="number" value={form.min_amount} onChange={(e) => setForm({ ...form, min_amount: e.target.value })} /></div>
                <div className="space-y-2"><Label>Max Amount</Label><Input type="number" value={form.max_amount} onChange={(e) => setForm({ ...form, max_amount: e.target.value })} /></div>
                <div className="space-y-2"><Label>Approver Role</Label><Input value={form.approver_role} onChange={(e) => setForm({ ...form, approver_role: e.target.value })} /></div>
                <div className="space-y-2"><Label>Sequence</Label><Input type="number" value={form.sequence} onChange={(e) => setForm({ ...form, sequence: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={addMatrix} className="bg-blue-600 hover:bg-blue-700" data-testid="matrix-submit">Add Rule</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Transaction</TableHead><TableHead>Amount Range</TableHead><TableHead>Approver</TableHead><TableHead>Seq</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody data-testid="matrix-table">
              {matrix.map((m) => (
                <TableRow key={m.id}>
                  <TableCell className="text-sm">{m.transaction_type}</TableCell>
                  <TableCell className="text-xs font-mono text-slate-600">{m.max_amount ? `₱${Number(m.min_amount).toLocaleString()} – ₱${Number(m.max_amount).toLocaleString()}` : "—"}</TableCell>
                  <TableCell className="text-sm">{m.approver_role}</TableCell>
                  <TableCell className="text-sm">{m.sequence}</TableCell>
                  <TableCell className="text-right"><Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => delMatrix(m)} data-testid={`del-matrix-${m.id}`}><Icons.Trash2 className="h-4 w-4" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Add Company ---------------- */
function CompanyTab() {
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({ name: "", code: "", short_name: "" });
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/companies").then(({ data }) => setCompanies(data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await api.post("/admin/companies", form); toast.success("Company added"); setForm({ name: "", code: "", short_name: "" }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const saveEdit = async () => {
    try { await api.put(`/admin/companies/${editing.id}`, { name: editing.name, code: editing.code, short_name: editing.short_name }); toast.success("Company updated"); setEditing(null); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <div className="bg-white rounded-xl border border-slate-200 p-5 h-fit">
        <div className="text-sm font-medium text-slate-800 mb-4">Add Company</div>
        <div className="space-y-3">
          <div className="space-y-2"><Label>Company Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="company-name" /></div>
          <div className="space-y-2"><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. PVX" data-testid="company-code" /></div>
          <div className="space-y-2"><Label>Short Name</Label><Input value={form.short_name} onChange={(e) => setForm({ ...form, short_name: e.target.value })} data-testid="company-shortname" /></div>
          <Button onClick={create} disabled={!form.name || !form.code} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="company-create">Add Company</Button>
        </div>
      </div>
      <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b text-sm font-medium text-slate-800">Companies ({companies.length})</div>
        <div className="divide-y divide-slate-100" data-testid="companies-list">
          {companies.map((c) => (
            <div key={c.id} className="px-5 py-3 flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center"><Icons.Building2 className="h-5 w-5" /></div>
              <div className="flex-1"><div className="text-sm font-medium text-slate-800">{c.name}</div><div className="text-xs text-slate-500">{c.short_name}</div></div>
              <Badge variant="outline" className="font-mono text-xs">{c.code}</Badge>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing({ ...c })} data-testid={`company-edit-${c.code}`}><Icons.Pencil className="h-4 w-4 text-slate-500" /></Button>
            </div>
          ))}
        </div>
      </div>
      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Company</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="space-y-2"><Label>Company Name</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="company-edit-name" /></div>
              <div className="space-y-2"><Label>Code</Label><Input value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value })} data-testid="company-edit-code" /></div>
              <div className="space-y-2"><Label>Short Name</Label><Input value={editing.short_name} onChange={(e) => setEditing({ ...editing, short_name: e.target.value })} data-testid="company-edit-shortname" /></div>
            </div>
          )}
          <DialogFooter><Button onClick={saveEdit} className="bg-blue-600 hover:bg-blue-700" data-testid="company-edit-save">Save Changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Branding + Security (settings) ---------------- */
function SettingsForm({ fields, testidPrefix }) {
  const { settings, loadSettings } = useAuth();
  const [form, setForm] = useState({});
  useEffect(() => { if (settings) setForm(settings); }, [settings]);
  const save = async () => {
    const payload = {}; fields.forEach((f) => { payload[f.key] = f.type === "number" ? Number(form[f.key]) : form[f.key]; });
    try { await api.put("/admin/settings", payload); toast.success("Settings saved"); loadSettings(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl">
      <div className="grid sm:grid-cols-2 gap-4">
        {fields.map((f) => (
          <div key={f.key} className={`space-y-2 ${f.full ? "sm:col-span-2" : ""}`}>
            <Label>{f.label}</Label>
            <Input type={f.type || "text"} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`${testidPrefix}-${f.key}`} />
          </div>
        ))}
      </div>
      <Button onClick={save} className="mt-5 bg-blue-600 hover:bg-blue-700" data-testid={`${testidPrefix}-save`}>Save Changes</Button>
    </div>
  );
}

/* ---------------- Data Management ---------------- */
function DataTab() {
  return <DataManagement allowPurge />;
}

/* ---------------- Audit Trail ---------------- */
function AuditTab() {
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");
  const [module, setModule] = useState("all");
  const load = useCallback(() => {
    const p = new URLSearchParams(); if (q) p.set("q", q); if (module !== "all") p.set("module", module);
    api.get(`/admin/audit-logs?${p.toString()}`).then(({ data }) => setLogs(data));
  }, [q, module]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const actionColor = { LOGIN: "bg-blue-50 text-blue-700", LOGOUT: "bg-slate-100 text-slate-600", CREATE: "bg-emerald-50 text-emerald-700", UPDATE: "bg-amber-50 text-amber-700", DELETE: "bg-red-50 text-red-700", APPROVE: "bg-emerald-50 text-emerald-700", REJECT: "bg-red-50 text-red-700", VIEW: "bg-slate-100 text-slate-600" };
  return (
    <div>
      <div className="flex gap-3 mb-4">
        <div className="relative flex-1 max-w-sm"><Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search user, record…" className="pl-9" data-testid="audit-search" /></div>
        <Select value={module} onValueChange={setModule}>
          <SelectTrigger className="w-48" data-testid="audit-module-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{["all", "Auth", "Leave", "User", "Role", "Company", "Payroll", "Settings", "Security", "DataManagement", "ApprovalMatrix", "ChangeRequest"].map((m) => <SelectItem key={m} value={m}>{m === "all" ? "All Modules" : m}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" onClick={async () => {
          const p = new URLSearchParams(); if (q) p.set("q", q); if (module !== "all") p.set("module", module);
          try { const res = await api.get(`/admin/audit-logs/export?${p.toString()}`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data])); const a = document.createElement("a");
            a.href = url; a.download = `audit-${module}.csv`; a.click(); URL.revokeObjectURL(url);
          } catch (e) {} }} data-testid="audit-export-btn"><Icons.Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead>Timestamp</TableHead><TableHead>User</TableHead><TableHead>Action</TableHead><TableHead>Module</TableHead><TableHead className="hidden lg:table-cell">Record</TableHead><TableHead className="hidden lg:table-cell">IP</TableHead>
          </TableRow></TableHeader>
          <TableBody data-testid="audit-table">
            {logs.map((l) => (
              <TableRow key={l.id}>
                <TableCell className="text-xs text-slate-500 whitespace-nowrap">{fmtDateTime(l.timestamp)}</TableCell>
                <TableCell className="text-sm">{l.user_name}</TableCell>
                <TableCell><Badge className={`text-[10px] ${actionColor[l.action] || "bg-slate-100"} hover:opacity-100`}>{l.action}</Badge></TableCell>
                <TableCell className="text-sm text-slate-600">{l.module}</TableCell>
                <TableCell className="hidden lg:table-cell font-mono text-[11px] text-slate-400 max-w-[160px] truncate">{l.record_id || "—"}</TableCell>
                <TableCell className="hidden lg:table-cell text-xs text-slate-400">{l.ip_address || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

/* ---------------- Tech Handbook ---------------- */
function HandbookTab() {
  const cards = [
    ["System Architecture & Modules", "High-level architectural schematic covering HRIS, Finance, Fleet & Vessel integrations.", Icons.Network, "bg-blue-50 text-blue-600"],
    ["Security & Compliance Protocols", "RBAC rules, 2FA policy enforcement, and audit trail indexing standards.", Icons.ShieldCheck, "bg-emerald-50 text-emerald-600"],
    ["Workflow Engine & Approval Matrix", "Multi-tier approval delegation for leave, vouchers, and procurement POs.", Icons.GitBranch, "bg-purple-50 text-purple-600"],
    ["Data Dictionary & Schema", "Field-level definitions for Employee Master, Payroll GL codes, and Assets.", Icons.Table2, "bg-amber-50 text-amber-600"],
    ["API & Webhook Documentation", "REST endpoints, OAuth2 scopes, payload schemas, and webhook retry policies.", Icons.Webhook, "bg-indigo-50 text-indigo-600"],
    ["Form Branding & UI Standards", "Company logos, CSS overrides, email templates, and PDF header configs.", Icons.Palette, "bg-rose-50 text-rose-600"],
  ];
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
      {cards.map(([t, d, I, c]) => (
        <div key={t} className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-shadow" data-testid="handbook-card">
          <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${c} mb-4`}><I className="h-[22px] w-[22px]" /></div>
          <div className="font-semibold text-slate-900">{t}</div>
          <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{d}</p>
          <button onClick={() => toast.info("Documentation module — coming soon")} className="text-sm text-blue-600 font-medium mt-4 inline-flex items-center gap-1 hover:gap-2 transition-all">Open <Icons.ArrowRight className="h-4 w-4" /></button>
        </div>
      ))}
    </div>
  );
}

export default function Settings() {
  const { hasPerm, user } = useAuth();
  const [tab, setTab] = useState("overview");
  const adminPerms = ["admin.users", "admin.roles", "admin.companies", "admin.workflow",
                      "admin.approval_matrix", "admin.settings"];
  if (!adminPerms.some((pp) => hasPerm(pp))) {
    return (
      <div className="uemp-fade-in">
        <PageHeader title="Master Settings" subtitle="System administration and global configuration." />
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-500" data-testid="settings-no-access">
          <Icons.Lock className="h-8 w-8 mx-auto mb-2 text-slate-300" />
          You don't have permission to access Master Settings.
        </div>
      </div>
    );
  }
  const visible = (k) => k !== "modules" || hasPerm("*");
  const cur = ALL.find(([k]) => k === tab);
  const NavBtn = ({ k, label, Icon }) => (
    <button onClick={() => setTab(k)} className={`w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors ${tab === k ? "bg-blue-50 text-blue-700 font-medium" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`} data-testid={`settings-tab-${k}`}>
      <Icon className="h-4 w-4 shrink-0" /><span className="truncate">{label}</span>
    </button>
  );
  const downloadDoc = async () => {
    try {
      const r = await api.get("/admin/system-documentation", { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "PIVI_ERP_Architecture_and_Processes.pdf"; a.click(); URL.revokeObjectURL(a.href);
    } catch { toast.error("Download failed"); }
  };
  return (
    <div className="uemp-fade-in">
      <PageHeader title="Master Settings" subtitle="Manage people, access, companies, communication, branding and system security in one place."
        actions={(user?.role === "SUPER ADMIN" || hasPerm("admin.settings")) && <Button variant="outline" size="sm" onClick={downloadDoc} data-testid="settings-download-doc"><Icons.FileDown className="h-4 w-4 mr-1.5" />System Documentation</Button>} />
      <div className="lg:hidden mb-4">
        <select value={tab} onChange={(e) => setTab(e.target.value)} className="w-full border border-slate-200 rounded-lg h-10 px-3 text-sm bg-white" data-testid="settings-mobile-nav">
          <option value="overview">Overview</option>
          {GROUPS.map((g) => <optgroup key={g.name} label={g.name}>{g.items.filter(([k]) => visible(k)).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</optgroup>)}
        </select>
      </div>
      <div className="flex gap-6">
        <aside className="hidden lg:block w-56 shrink-0">
          <nav className="sticky top-4 bg-white rounded-xl border border-slate-200 p-2 space-y-3" data-testid="settings-nav">
            <NavBtn k="overview" label="Overview" Icon={Icons.LayoutDashboard} />
            {GROUPS.map((g) => {
              const items = g.items.filter(([k]) => visible(k));
              if (!items.length) return null;
              return (
                <div key={g.name}>
                  <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{g.name}</div>
                  {items.map(([k, l, I]) => <NavBtn key={k} k={k} label={l} Icon={I} />)}
                </div>
              );
            })}
          </nav>
        </aside>
        <div className="min-w-0 flex-1">
          {cur && (
            <div className="flex items-start gap-3 mb-5 pb-4 border-b border-slate-200" data-testid="settings-section-header">
              <div className={`h-10 w-10 shrink-0 rounded-lg flex items-center justify-center ${cur[4]}`}>{React.createElement(cur[2], { className: "h-5 w-5" })}</div>
              <div><h2 className="text-lg font-semibold text-slate-900">{cur[1]}</h2><p className="text-sm text-slate-500">{cur[3]}</p></div>
            </div>
          )}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsContent value="overview" className="mt-0"><Overview go={setTab} visible={visible} /></TabsContent>
        <TabsContent value="users"><UsersTab /></TabsContent>
        {hasPerm("*") && <TabsContent value="modules"><ModuleAccessTab /></TabsContent>}
        <TabsContent value="roles"><RolesTab /></TabsContent>
        <TabsContent value="workflow"><WorkflowTab /></TabsContent>
        <TabsContent value="chat"><ChatSettings /></TabsContent>
        <TabsContent value="announcements"><AnnouncementsAdmin /></TabsContent>
        <TabsContent value="company"><CompanyTab /></TabsContent>
        <TabsContent value="branding">
          <SettingsForm testidPrefix="branding" fields={[
            { key: "company_name", label: "Company Name", full: true },
            { key: "system_name", label: "System Name", full: true },
            { key: "short_name", label: "Short Name" },
            { key: "primary_color", label: "Primary Color" },
            { key: "contact_email", label: "Contact Email" },
            { key: "contact_phone", label: "Contact Phone" },
            { key: "footer_disclaimer", label: "Footer Disclaimer", full: true },
          ]} />
        </TabsContent>
        <TabsContent value="security">
          <div className="space-y-5">
            <SettingsForm testidPrefix="security" fields={[
              { key: "session_idle_minutes", label: "Session Idle Timeout (min)", type: "number" },
              { key: "password_min_length", label: "Password Min Length", type: "number" },
              { key: "max_failed_attempts", label: "Max Failed Attempts", type: "number" },
              { key: "lockout_minutes", label: "Lockout Duration (min)", type: "number" },
              { key: "audit_retention_days", label: "Audit Retention (days)", type: "number" },
            ]} />
            <MfaCard />
          </div>
        </TabsContent>
        <TabsContent value="data"><DataTab /></TabsContent>
        <TabsContent value="audit"><AuditTab /></TabsContent>
        <TabsContent value="handbook"><HandbookTab /></TabsContent>
      </Tabs>
        </div>
      </div>
    </div>
  );
}
